import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Printer, FileText, ChevronLeft, ChevronRight, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { DocumentPreview } from "./DocumentPreview";
import { printMailPackage } from "@/utils/mailPackagePrint";
import { generateMailPackageWord } from "@/utils/mailPackageWordGenerator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

interface Deal {
  id: string;
  deal_title: string;
  seller_name: string;
  buyer_name: string;
  property_address: string;
  offer_price?: number;
  sales_price?: number;
  current_balance?: number;
  payment_amount?: number;
  interest_rate?: number;
  seller_address?: string;
}

interface DocumentType {
  id: string;
  label: string;
  enabled: boolean;
}

interface PackageBuilderProps {
  onPackageSaved?: () => void;
}

export function PackageBuilder({ onPackageSaved }: PackageBuilderProps) {
  const { toast } = useToast();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [notes, setNotes] = useState("");

  const [documents, setDocuments] = useState<DocumentType[]>([
    { id: "coverLetter", label: "Cover Letter", enabled: true },
    { id: "agreement", label: "Purchase Agreement", enabled: true },
    { id: "verification", label: "Contract Verification Form", enabled: true },
    { id: "respa", label: "RESPA Compliance Form", enabled: true },
    { id: "checklist", label: "Checklist Letter", enabled: true },
  ]);

  useEffect(() => {
    loadDeals();
  }, []);

  useEffect(() => {
    if (selectedDealId) {
      const deal = deals.find(d => d.id === selectedDealId);
      setSelectedDeal(deal || null);
    } else {
      setSelectedDeal(null);
    }
  }, [selectedDealId, deals]);

  const loadDeals = async () => {
    const { data, error } = await supabase
      .from("deals")
      .select("id, deal_title, seller_name, buyer_name, property_address, offer_price, sales_price, current_balance, payment_amount, interest_rate, seller_address")
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        title: "Error loading deals",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setDeals(data || []);
  };

  const toggleDocument = (docId: string) => {
    setDocuments(docs =>
      docs.map(doc =>
        doc.id === docId ? { ...doc, enabled: !doc.enabled } : doc
      )
    );
  };

  const enabledDocuments = documents.filter(d => d.enabled);

  const handlePrintPackage = async () => {
    if (!selectedDealId) {
      toast({
        title: "No deal selected",
        description: "Please select a deal first",
        variant: "destructive",
      });
      return;
    }

    await printMailPackage(selectedDealId, enabledDocuments.map(d => d.id));
  };

  const handleExportWord = async () => {
    if (!selectedDealId) {
      toast({
        title: "No deal selected",
        description: "Please select a deal first",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const blob = await generateMailPackageWord(selectedDealId, enabledDocuments.map(d => d.id));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mail-package-${selectedDeal?.deal_title || 'package'}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Word document generated",
        description: "Your mail package has been downloaded",
      });
    } catch (error) {
      toast({
        title: "Error generating Word document",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePackage = async () => {
    if (!selectedDealId) {
      toast({
        title: "No deal selected",
        description: "Please select a deal first",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("mail_packages")
        .insert([{
          deal_id: selectedDealId,
          documents_included: enabledDocuments.map(d => d.id) as any,
          package_configuration: { documents } as any,
          printed_date: new Date().toISOString(),
          tracking_number: trackingNumber || null,
          notes: notes || null,
        }]);

      if (error) throw error;

      toast({
        title: "Package saved",
        description: "Mail package has been logged successfully",
      });

      setTrackingNumber("");
      setNotes("");
      onPackageSaved?.();
    } catch (error) {
      toast({
        title: "Error saving package",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - Controls */}
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Select Deal</CardTitle>
            <CardDescription>Choose the deal to prepare documents for</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedDealId} onValueChange={setSelectedDealId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a deal..." />
              </SelectTrigger>
              <SelectContent>
                {deals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.deal_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedDeal && (
              <div className="mt-4 p-3 bg-muted rounded-md space-y-1 text-sm">
                <p><strong>Seller:</strong> {selectedDeal.seller_name}</p>
                <p><strong>Buyer:</strong> {selectedDeal.buyer_name}</p>
                <p><strong>Property:</strong> {selectedDeal.property_address}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Select Documents</CardTitle>
            <CardDescription>Choose which documents to include</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center space-x-2">
                <Checkbox
                  id={doc.id}
                  checked={doc.enabled}
                  onCheckedChange={() => toggleDocument(doc.id)}
                />
                <Label htmlFor={doc.id} className="cursor-pointer">
                  {doc.label}
                </Label>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Package Details</CardTitle>
            <CardDescription>Optional tracking information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="tracking">Tracking Number</Label>
              <Input
                id="tracking"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="USPS, FedEx, etc."
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              onClick={handlePrintPackage}
              disabled={!selectedDealId || enabledDocuments.length === 0}
              className="w-full"
            >
              <Printer className="mr-2 h-4 w-4" />
              Print Complete Package
            </Button>
            <Button
              onClick={handleExportWord}
              disabled={!selectedDealId || enabledDocuments.length === 0 || loading}
              variant="outline"
              className="w-full"
            >
              <FileText className="mr-2 h-4 w-4" />
              Export to Word
            </Button>
            <Button
              onClick={handleSavePackage}
              disabled={!selectedDealId || enabledDocuments.length === 0 || loading}
              variant="secondary"
              className="w-full"
            >
              <Save className="mr-2 h-4 w-4" />
              Save Package Log
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Right Column - Preview */}
      <div className="lg:col-span-2">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Document Preview</CardTitle>
                <CardDescription>
                  Page {currentPage + 1} of {enabledDocuments.length}
                  {enabledDocuments[currentPage] ? ` - ${enabledDocuments[currentPage].label}` : ""}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(p => Math.min(enabledDocuments.length - 1, p + 1))}
                  disabled={currentPage === enabledDocuments.length - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {selectedDealId && enabledDocuments.length > 0 ? (
              <DocumentPreview
                dealId={selectedDealId}
                documentType={enabledDocuments[currentPage].id}
              />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                {!selectedDealId
                  ? "Select a deal to preview documents"
                  : "Select at least one document to preview"}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
